import { AppDispatch } from '@/store';
import { gameActions } from '@/store/reducers/game';
import { unitActions } from '@/store/reducers/unit';
import { activityLocationsActions } from '@/store/reducers/activityLocations';
import unitThunks from '@/store/thunks/units';
import unitTopicsThunks from '@/store/thunks/unit-topics';
import quizzesThunks from '@/store/thunks/quizzes';
import commonUtils from '@/utils/common';
import { ImprovableRecentActivity } from '@/types/progress';
import { SkillSettingsItem } from '@/types/user';
import { SkillTypes } from '@/types/skill';
import { UnitsState } from '@/types/units';

export interface GetQuizByActivityParams {
  activity: ImprovableRecentActivity;
  dispatch: AppDispatch;
  navigate: (path: string) => void;
  selectedType: SkillTypes | null;
  skillSettings: SkillSettingsItem[];
  selectedUnit: UnitsState['selectedUnit'];
  gameSelectedTopic: any;
}

/**
 * Resolves and opens the quiz for an "improve your last activities" entry.
 * Mirrors the roadmap -> topic drawer -> quiz flow (same thunks/actions) so
 * unitId/topic resolution and last-activity counting behave identically
 * regardless of which entry point the student used (see ELL-8132).
 */
export const getQuizByActivity = async ({
  activity,
  dispatch,
  navigate,
  selectedType,
  skillSettings,
  selectedUnit,
  gameSelectedTopic,
}: GetQuizByActivityParams): Promise<void> => {
  // Snapshot so we can roll back if the quiz turns out missing/stale.
  // refreshTopicQuizzes needs selectedUnit/game.selectedTopic committed to
  // the store before it runs, but a failed lookup shouldn't leave the app
  // pointed at a topic the student never actually opened.
  const previousSelectedUnit = selectedUnit;
  const previousGameSelectedTopic = gameSelectedTopic;

  const rollbackSelection = () => {
    dispatch(
      unitActions.setSelectedUnit({
        id: previousSelectedUnit.id,
        name: previousSelectedUnit.name,
        order: previousSelectedUnit.order,
      })
    );
    dispatch(gameActions.setSelectedTopic(previousGameSelectedTopic));
  };

  try {
    const { level, unitId, topicId, activityId } = activity;

    if (!selectedType && skillSettings.length) {
      const firstSkill = skillSettings[0];
      dispatch(
        gameActions.setSelectedSkill({
          type: commonUtils.skillByName(firstSkill.name ?? ''),
          skill: firstSkill,
        })
      );
    }

    const [{ units }, { topics }] = await Promise.all([
      dispatch(unitThunks.fetchUnits({ level })).unwrap(),
      dispatch(unitTopicsThunks.fetchUnitTopics({ level, unitId })).unwrap(),
    ]);

    const unit = units?.find((obj: { id: number }) => obj.id === unitId);
    const topic = topics?.find((obj: { id: number }) => obj.id === topicId);

    if (!topic || !unit) {
      console.error('Topic or Unit not found');
      commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
      return;
    }

    dispatch(
      unitActions.setSelectedUnit({
        id: unit.id,
        name: unit.name,
        order: unit.order,
      })
    );
    dispatch(gameActions.setSelectedTopic(topic));

    const quizzes = await dispatch(quizzesThunks.refreshTopicQuizzes({ topicId })).unwrap();
    const quiz = quizzes?.find((obj: { id: number }) => obj.id === activityId);

    if (!quiz) {
      rollbackSelection();
      console.error('Quiz not found');
      commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
      return;
    }

    dispatch(
      unitActions.setSelectedTopic({
        id: topic.id,
        name: topic.name,
        cEFRDescriptor: topic.cEFRDescriptor,
        canDoStatement: topic.canDoStatement,
      })
    );
    dispatch(gameActions.setSelectedQuiz(quiz));
    dispatch(
      activityLocationsActions.setActivityLocation({
        activityId: quiz.id,
        topic: {
          id: topic.id,
          name: topic.name,
          cEFRDescriptor: topic.cEFRDescriptor,
          canDoStatement: topic.canDoStatement,
        },
        activitiesInTopic: topic.activitiesInTopic,
        unit: {
          id: unit.id,
          name: unit.name,
          order: unit.order,
        },
        level,
      })
    );
    navigate('/dashboard/quiz');
  } catch (error) {
    rollbackSelection();
    console.error('Error in getQuizByActivity:', error);
    commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
  }
};

export default getQuizByActivity;
